import axios, { type AxiosInstance } from "axios"
import { getExhibitionHeader } from "../utils/exhibitionContext";
import { format } from "date-fns"
import type {
  ExhibitorSummaryStats,
  PaginatedExhibitorRepresentativeAttendance,
  PaginatedExhibitorVehicleAttendance,
} from "../Component/Dashboard/exhibitorReports/types"

const api: AxiosInstance = axios.create({
  baseURL: process.env.REACT_APP_API_URL || "http://127.0.0.1:8000",
  headers: {
    "Content-Type": "application/json",
  },
  timeout: 10000,
})

// Attach token before each request
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("accessToken")
    if (token) {
      config.headers["Authorization"] = `Bearer ${token}`
    
    Object.assign(config.headers, getExhibitionHeader());}
    return config
  },
  (error) => Promise.reject(error),
)

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem("accessToken")
      window.location.href = "/auth/login/"
    }
    return Promise.reject(error)
  },
)

const ExhibitorDashboardService = {
  async getExhibitorSummaryStats(start_date?: Date, end_date?: Date): Promise<ExhibitorSummaryStats> {
    try {
      let url = `/api/exhibitorsaccess/exhibitor-summary-stats/`
      const params = new URLSearchParams()
      if (start_date) {
        params.append("start_date", format(start_date, "yyyy-MM-dd"))
      }
      if (end_date) {
        params.append("end_date", format(end_date, "yyyy-MM-dd"))
      }
      if (params.toString()) {
        url += `?${params.toString()}`
      }
      const response = await api.get(url)
      return response.data
    } catch (error) {
      console.error("Error fetching exhibitor summary stats:", error)
      throw new Error("Failed to fetch exhibitor summary statistics")
    }
  },

  // Adjusted getExhibitorRepresentativeAttendance to pass params as an object
  async getExhibitorRepresentativeAttendance(params: {
    page?: number
    page_size?: number
    start_date?: string
    end_date?: string
    search?: string
  }) {
    try {
      const response = await api.get("/api/badges/exhibitor-representative-attendance/", { params })
      return response.data
    } catch (error) {
      console.error("Error fetching exhibitor representative attendance:", error)
      throw new Error("Failed to fetch exhibitor representative attendance")
    }
  },

  // Adjusted exportExhibitorRepresentativeAttendance to pass params as an object
  async exportExhibitorRepresentativeAttendance(params: {
    start_date?: string
    end_date?: string
    search?: string
  }) {
    try {
      const response = await api.get("/api/badges/export-exhibitor-representative-attendance/", {
        params,
        responseType: "blob",
      })
      return response.data
    } catch (error) {
      console.error("Error exporting exhibitor representative attendance:", error)
      throw new Error("Failed to export exhibitor representative attendance")
    }
  },

  // Updated getExhibitorVehicleAttendance to accept params as an object, similar to representatives
  async getExhibitorVehicleAttendance(params: {
    page?: number
    page_size?: number
    start_date?: Date
    end_date?: Date
    search?: string
  }): Promise<PaginatedExhibitorVehicleAttendance> {
    try {
      const { page = 1, page_size = 10, start_date, end_date, search } = params

      const formattedParams: any = {
        page,
        page_size,
        start_date: start_date ? format(start_date, "yyyy-MM-dd") : undefined,
        end_date: end_date ? format(end_date, "yyyy-MM-dd") : undefined,
        search,
      }

      const response = await api.get("/api/entries/exhibitor-vehicle-attendance/", { params: formattedParams })
      return response.data
    } catch (error) {
      console.error("Error fetching exhibitor vehicle attendance:", error)
      throw new Error("Failed to fetch exhibitor vehicle attendance")
    }
  },

  // Updated exportExhibitorVehicleAttendance to accept params as an object, similar to representatives
  async exportExhibitorVehicleAttendance(params: {
    start_date?: Date
    end_date?: Date
    search?: string
  }): Promise<Blob> {
    try {
      const formattedParams: any = {
        start_date: params.start_date ? format(params.start_date, "yyyy-MM-dd") : undefined,
        end_date: params.end_date ? format(params.end_date, "yyyy-MM-dd") : undefined,
        search: params.search,
      }

      const response = await api.get("/api/entries/export-exhibitor-vehicle-attendance/", {
        params: formattedParams,
        responseType: "blob",
      })
      return response.data
    } catch (error) {
      console.error("Error exporting exhibitor vehicle attendance:", error)
      throw new Error("Failed to export exhibitor vehicle attendance")
    }
  },
}

export default ExhibitorDashboardService
